import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '../lib/i18n';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import usePasteUpload from '../hooks/usePasteUpload';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Loader2,
  Shield,
  FileCheck,
  Wrench,
  Car,
  Calendar,
  AlertTriangle,
  X,
  Eye,
  File,
  Image as ImageIcon,
  ZoomIn,
  ZoomOut,
  RotateCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DOC_TYPES = [
  { value: 'keuring', label: 'Keuring', icon: FileCheck, color: 'text-blue-600' },
  { value: 'ruhsat', label: 'Ruhsat', icon: Car, color: 'text-blue-600' },
  { value: 'sigorta_policesi', label: 'Sigorta Poliçesi', icon: Shield, color: 'text-emerald-600' },
  { value: 'muayene_raporu', label: 'Muayene Raporu', icon: FileCheck, color: 'text-purple-600' },
  { value: 'bakim_raporu', label: 'Bakım Raporu', icon: Wrench, color: 'text-amber-600' },
  { value: 'diger', label: 'Diğer', icon: FileText, color: 'text-slate-600' }
];

export default function VehicleDocuments({ vehicleId, vehiclePlate, vehicleFirmId, assignedVergunningCodes = [], onClose, onChanged }) {
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);
  
  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    doc_type: 'ruhsat',
    doc_name: '',
    expiry_date: '',
    notes: '',
    vergunning_number: '',
    vergunning_code: '',
    file: null
  });

  // Fetch vergunning codes for dropdown.
  // If this vehicle is attached to a firm, filter to that firm's codes so
  // the user only picks from the relevant set (still shows existing vehicle-
  // assigned codes even if they come from another firm, to be safe).
  const [vergCodes, setVergCodes] = useState([]);
  useEffect(() => {
    api.get('/vergunning-codes').then(res => {
      if (res.data?.success) {
        const all = res.data.data || [];
        let filtered = all;
        if (vehicleFirmId) {
          filtered = all.filter(c => c.firm_id === vehicleFirmId);
        }
        // Ensure every code already assigned to the vehicle is selectable
        // even if it belongs to another firm (legacy data protection).
        const visibleCodes = new Set(filtered.map(c => c.code));
        const extra = all.filter(c => assignedVergunningCodes.includes(c.code) && !visibleCodes.has(c.code));
        setVergCodes([...filtered, ...extra]);
      }
    }).catch(() => {});
  }, [vehicleFirmId, assignedVergunningCodes]);

  // Helper functions for file type detection
  const isImageFile = (doc) => {
    const type = doc?.content_type?.toLowerCase() || '';
    return type.startsWith('image/');
  };

  const isPdfFile = (doc) => {
    const type = doc?.content_type?.toLowerCase() || '';
    const filename = doc?.original_filename?.toLowerCase() || doc?.doc_name?.toLowerCase() || '';
    return type === 'application/pdf' || filename.endsWith('.pdf');
  };

  const isPreviewable = (doc) => isImageFile(doc) || isPdfFile(doc);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/vehicles/${vehicleId}/documents`);
      if (response.data.success) {
        setDocuments(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Belgeler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    if (vehicleId) {
      fetchDocuments();
    }
  }, [vehicleId, fetchDocuments]);

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setUploadForm({ ...uploadForm, file: files[0], doc_name: files[0].name });
      setUploadDialog(true);
    }
  };

  // Handle file select
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({ ...uploadForm, file, doc_name: file.name });
      setUploadDialog(true);
    }
    // Reset so same file can be re-selected
    e.target.value = '';
  };

  // ─── Ctrl+V (Paste) → Belge yükleme dialog'unu pano dosyasıyla aç ──────────
  // Davranış birebir handleFileSelect/handleDrop gibi: dosyayı uploadForm'a koy,
  // metadata dolduran dialog'u göster (kullanıcı doc_type/expiry vb. tamamlayıp
  // "Yükle"ye basacak). Tek dosya alınır; çoklu paste'in ilk dosyası kullanılır.
  usePasteUpload((files) => {
    const file = files[0];
    if (!file) return;
    setUploadForm({ ...uploadForm, file, doc_name: file.name });
    setUploadDialog(true);
  }, { enabled: !!vehicleId, accept: 'image/*,.pdf,.jpg,.jpeg,.png,.doc,.docx' });

  // Upload document
  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error('Dosya seçiniz');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('doc_type', uploadForm.doc_type);
      formData.append('doc_name', uploadForm.doc_name || uploadForm.file.name);
      if (uploadForm.expiry_date) {
        formData.append('expiry_date', uploadForm.expiry_date);
      }
      if (uploadForm.notes) {
        formData.append('notes', uploadForm.notes);
      }

      const response = await api.post(`/vehicles/${vehicleId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success('Belge yüklendi');
        setUploadDialog(false);
        setUploadForm({
          doc_type: 'ruhsat',
          doc_name: '',
          expiry_date: '',
          notes: '',
          vergunning_number: '',
          vergunning_code: '',
          file: null
        });
        // Reset file input so the same file or new file can be selected again
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchDocuments();
        // Notify parent so vehicles table refreshes its vergunning chips.
        if (onChanged) onChanged();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yükleme hatası');
    } finally {
      setUploading(false);
    }
  };

  // Download document
  const handleDownload = async (doc) => {
    // Şub 27, 2026 — Direct CDN URL varsa doğrudan indir, yoksa blob fallback
    const filename = doc.file_name || doc.original_filename || doc.doc_name || 'document';
    try {
      const pres = await api.get(`/vehicles/${vehicleId}/documents/${doc.id}/download-url?preview=false`);
      const directUrl = pres.data?.data?.url;
      if (directUrl) {
        const link = document.createElement('a');
        link.href = directUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }
    } catch (_) { /* fallback */ }
    try {
      const response = await api.get(`/vehicles/${vehicleId}/documents/${doc.id}/download?preview=true`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('İndirme hatası');
    }
  };

  // Preview document
  const handlePreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setZoom(1);
    setRotation(0);
    setPdfPageNumber(1);
    setPdfNumPages(null);
    setPreviewUrl(null);

    try {
      // Şub 27, 2026 — CORS-safe: sadece image direct CDN;
      // PDF/DOC için CORS yapılana kadar blob fetch
      try {
        const pres = await api.get(`/vehicles/${vehicleId}/documents/${doc.id}/download-url?preview=true`);
        const directUrl = pres.data?.data?.url;
        const ct = (pres.data?.data?.content_type || '').toLowerCase();
        if (directUrl && ct.startsWith('image/')) {
          setPreviewUrl(directUrl);
          setPreviewLoading(false);
          return;
        }
      } catch (_) { /* fallback */ }

      const response = await api.get(`/vehicles/${vehicleId}/documents/${doc.id}/download?preview=true`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: doc.content_type });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Preview error:', error);
      // Backend bazı durumlarda 410 Gone döner (orijinal dosyası eski
      // sunucudan taşınmamış) → kullanıcıya net mesaj göster.
      let msg = 'Önizleme yüklenemedi';
      try {
        if (error.response?.data instanceof Blob) {
          const txt = await error.response.data.text();
          const json = JSON.parse(txt);
          msg = json.detail || msg;
        } else if (error.response?.data?.detail) {
          msg = error.response.data.detail;
        }
      } catch { /* parse hatası — default msg kullan */ }
      setPreviewError(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Close preview and cleanup
  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewOpen(false);
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewError(null);
  };

  // PDF load success handler
  const onPdfLoadSuccess = ({ numPages }) => {
    setPdfNumPages(numPages);
  };

  // Delete document
  const handleDelete = async (docId) => {
    try {
      await api.delete(`/vehicles/${vehicleId}/documents/${docId}`);
      toast.success('Belge silindi');
      setDeleteConfirm(null);
      fetchDocuments();
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme hatası');
    }
  };

  // Get the effective category of a document (handles both doc_type and category fields)
  const getDocCategory = (doc) => {
    const dt = doc.doc_type;
    const cat = doc.category;
    // doc_type might be "NONE" string or missing
    if (dt && dt !== 'NONE' && dt !== 'none') return dt;
    if (cat) return cat;
    return 'diger';
  };

  // Get doc type info
  const getDocTypeInfo = (type) => {
    return DOC_TYPES.find(d => d.value === type) || DOC_TYPES[DOC_TYPES.length - 1];
  };

  // Check if document is expiring soon
  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Araç Belgeleri
          </h3>
          <p className="text-sm text-slate-500">
            <Badge variant="outline" className="font-mono">{vehiclePlate}</Badge> plakalı araca ait belgeler
          </p>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Vehicle's assigned vergunning codes (same chip style as main table) */}
      {assignedVergunningCodes && assignedVergunningCodes.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-md border border-teal-200 dark:border-teal-800">
          <span className="text-xs font-medium text-teal-700 dark:text-teal-300">
            Araca atanmış vergunning kodları:
          </span>
          {assignedVergunningCodes.map(code => (
            <Badge
              key={code}
              variant="secondary"
              className="bg-teal-100 text-teal-700 dark:bg-teal-800/40 dark:text-teal-200 font-mono text-[11px]"
            >
              {code}
            </Badge>
          ))}
        </div>
      )}

      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging 
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
            : 'border-slate-300 dark:border-slate-600 hover:border-blue-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="document-dropzone"
      >
        <Upload className={`h-10 w-10 mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-slate-400'}`} />
        <p className="text-slate-600 dark:text-slate-300 mb-2">
          Belgeleri sürükleyip bırakın · <span className="font-medium text-blue-600 dark:text-blue-400">Ctrl+V</span> ile yapıştırın veya
        </p>
        <label>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          />
          <Button variant="outline" className="cursor-pointer" asChild>
            <span>
              <Upload className="h-4 w-4 mr-2" />
              Dosya Seçin
            </span>
          </Button>
        </label>
        <p className="text-xs text-slate-400 mt-2">
          PDF, JPG, PNG, DOC dosyaları desteklenir (max 10MB)
        </p>
      </div>

      {/* Document Categories */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {DOC_TYPES.map((type) => {
          const Icon = type.icon;
          const catDocs = documents.filter(d => getDocCategory(d) === type.value);
          const count = catDocs.length;
          const vergNum = type.value === 'vergunning' && catDocs.length > 0 ? catDocs[0]?.vergunning_number : null;
          return (
            <Card 
              key={type.value} 
              className={`cursor-pointer hover:shadow-md transition-shadow ${count > 0 ? 'ring-1 ring-blue-200' : ''}`}
            >
              <CardContent className="p-3 text-center">
                <Icon className={`h-6 w-6 mx-auto mb-1 ${type.color}`} />
                <p className="text-xs font-medium">{type.label}</p>
                <Badge variant={count > 0 ? "default" : "secondary"} className="mt-1 text-xs">
                  {count}
                </Badge>
                {vergNum && (
                  <p className="text-[10px] text-teal-600 font-mono mt-0.5">#{vergNum}</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Documents List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Yüklü Belgeler ({documents.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="h-10 w-10 mx-auto mb-2 text-slate-300" />
              <p>Henüz belge yüklenmemiş</p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => {
                const typeInfo = getDocTypeInfo(getDocCategory(doc));
                const Icon = typeInfo.icon;
                const expired = isExpired(doc.expiry_date);
                const expiringSoon = isExpiringSoon(doc.expiry_date);
                
                return (
                  <div
                    key={doc.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      expired ? 'bg-red-50 border-red-200' :
                      expiringSoon ? 'bg-amber-50 border-amber-200' :
                      'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg bg-white dark:bg-slate-700 shadow-sm`}>
                        {isImageFile(doc) ? (
                          <ImageIcon className={`h-5 w-5 ${typeInfo.color}`} />
                        ) : (
                          <Icon className={`h-5 w-5 ${typeInfo.color}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{doc.doc_name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                          <Badge variant="outline" className="text-xs">{typeInfo.label}</Badge>
                          {doc.vergunning_code && (
                            <Badge variant="outline" className="border-teal-200 text-teal-700 bg-teal-50 text-xs font-mono">
                              {doc.vergunning_code}
                            </Badge>
                          )}
                          {doc.vergunning_number && (
                            <Badge variant="outline" className="border-indigo-200 text-indigo-700 bg-indigo-50 text-xs font-mono">
                              #{doc.vergunning_number}
                            </Badge>
                          )}
                          <span>{formatFileSize(doc.file_size)}</span>
                          {doc.expiry_date && (
                            <span className={`flex items-center gap-1 ${expired ? 'text-red-600' : expiringSoon ? 'text-amber-600' : ''}`}>
                              <Calendar className="h-3 w-3" />
                              {new Date(doc.expiry_date).toLocaleDateString('tr-TR')}
                              {expired && <AlertTriangle className="h-3 w-3" />}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {isPreviewable(doc) && (
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => handlePreview(doc)}
                          title="Önizle"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => handleDownload(doc)} title="İndir">
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteConfirm(doc)} title="Sil">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              Belge Yükle
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* File Info */}
            {uploadForm.file && (
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center gap-3">
                <File className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="font-medium text-sm">{uploadForm.file.name}</p>
                  <p className="text-xs text-slate-500">{formatFileSize(uploadForm.file.size)}</p>
                </div>
              </div>
            )}

            {/* Document Type */}
            <div>
              <Label>Belge Tipi *</Label>
              <Select
                value={uploadForm.doc_type}
                onValueChange={(v) => setUploadForm({ ...uploadForm, doc_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        <type.icon className={`h-4 w-4 ${type.color}`} />
                        {type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Document Name */}
            <div>
              <Label>Belge Adı</Label>
              <Input
                value={uploadForm.doc_name}
                onChange={(e) => setUploadForm({ ...uploadForm, doc_name: e.target.value })}
                placeholder="Örn: 2026 Sigorta Poliçesi"
              />
            </div>

            {/* Expiry Date */}
            <div>
              <Label className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Geçerlilik Tarihi
              </Label>
              <Input
                type="date"
                value={uploadForm.expiry_date}
                onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">Sigorta veya muayene bitiş tarihi</p>
            </div>

            {/* Notes */}
            <div>
              <Label>Notlar</Label>
              <Input
                value={uploadForm.notes}
                onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                placeholder="Ek bilgiler..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadForm.file}>
              {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Yükle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Belgeyi Sil</DialogTitle>
          </DialogHeader>
          <p className="py-4">
            <span className="font-medium">{deleteConfirm?.doc_name}</span> belgesini silmek istediğinize emin misiniz?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm?.id)}>
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={closePreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                {isPdfFile(previewDoc) ? (
                  <FileText className="h-5 w-5 text-red-500" />
                ) : (
                  <ImageIcon className="h-5 w-5 text-blue-500" />
                )}
                <span className="truncate max-w-md">{previewDoc?.doc_name || 'Önizleme'}</span>
                {isPdfFile(previewDoc) && pdfNumPages && (
                  <Badge variant="outline" className="ml-2">{pdfPageNumber} / {pdfNumPages}</Badge>
                )}
              </DialogTitle>
            </div>
          </DialogHeader>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 py-2 border-b flex-shrink-0">
            {isPdfFile(previewDoc) && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPdfPageNumber(p => Math.max(1, p - 1))}
                  disabled={pdfPageNumber <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">{pdfPageNumber} / {pdfNumPages || '?'}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPdfPageNumber(p => Math.min(pdfNumPages || p, p + 1))}
                  disabled={pdfPageNumber >= (pdfNumPages || 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <div className="border-l pl-4 flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setZoom(s => Math.max(0.5, s - 0.25))}>
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
                  <Button variant="outline" size="sm" onClick={() => setZoom(s => Math.min(2, s + 0.25))}>
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
            {isImageFile(previewDoc) && (
              <>
                <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.max(0.25, z - 0.25))}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
                <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.min(3, z + 0.25))}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setRotation(r => (r + 90) % 360)}>
                  <RotateCw className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-900 rounded-lg flex items-start justify-center min-h-[400px] p-4">
            {previewLoading ? (
              <div className="flex flex-col items-center gap-3 py-20">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <p className="text-sm text-slate-500">Yükleniyor...</p>
              </div>
            ) : previewError ? (
              <div className="text-center text-red-500 py-20">
                <p>{previewError}</p>
                <Button className="mt-4" variant="outline" onClick={() => previewDoc && handleDownload(previewDoc)}>
                  <Download className="h-4 w-4 mr-2" />
                  Dosyayı İndir
                </Button>
              </div>
            ) : isImageFile(previewDoc) && previewUrl ? (
              <div className="overflow-auto w-full h-full flex items-center justify-center">
                <img
                  src={previewUrl}
                  alt={previewDoc?.doc_name || 'Preview'}
                  className="max-w-full transition-transform duration-200"
                  style={{ 
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    transformOrigin: 'center center'
                  }}
                />
              </div>
            ) : isPdfFile(previewDoc) && previewUrl ? (
              <Document
                file={previewUrl}
                onLoadSuccess={onPdfLoadSuccess}
                onLoadError={(error) => {
                  console.error('PDF load error:', error);
                  setPreviewError('PDF yüklenemedi');
                }}
                loading={
                  <div className="flex items-center gap-2 py-20">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>PDF yükleniyor...</span>
                  </div>
                }
              >
                <Page
                  pageNumber={pdfPageNumber}
                  scale={zoom}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Document>
            ) : (
              <div className="text-center p-8 py-20">
                <FileText className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p className="text-slate-500 mb-4">Bu dosya türü önizlenemez</p>
                <Button onClick={() => previewDoc && handleDownload(previewDoc)}>
                  <Download className="h-4 w-4 mr-2" />
                  Dosyayı İndir
                </Button>
              </div>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <div className="flex justify-between items-center w-full">
              <span className="text-sm text-slate-500">
                {formatFileSize(previewDoc?.file_size || 0)}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={closePreview}>
                  Kapat
                </Button>
                <Button onClick={() => previewDoc && handleDownload(previewDoc)}>
                  <Download className="h-4 w-4 mr-2" />
                  İndir
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
